import { useEffect, useState, type ReactNode } from 'react'

import {
  ApiError,
  api,
  shortDate,
  type ConnectorField,
  type ConnectorInstance,
  type ConnectorSending,
  type ConnectorTemplate,
  type ProbeResult,
} from '../../lib/api'
import { useSave, type DialogSave } from '../../lib/save'
import { Dialog, DialogFooter, Status, Toggletip } from '../kit'
import { useToast } from '../ui'
import {
  FIELD_GROUPS,
  MAPPINGS,
  PROOF_NOTE,
  SETUP,
  STEPS,
  fieldApplies,
  type CatalogueCard,
  type Mapping,
  type Step,
} from './catalogue'

// One integration, opened from its card: what it does, how its fields map,
// how it is set up, whether it works, and the form that connects it.
//
// # WHY EVERYTHING THE OLD CARD DID IS HERE
//
// The card on the catalogue carries a name, one line, one status and one
// action, and nothing else. Every call the old card made (connect, save, test,
// issue a key, remove, send a test message) is made from this drawer instead,
// with the same arguments, so moving the controls moved nothing about what
// they do.
//
// # WHY A PLAN HAS NO FORM
//
// A plan is an integration that is designed and not built. Its drawer says what
// it will do and that it is not built, and offers nothing to fill in, because a
// form that saves a connection nothing will ever use is a promise the product
// cannot keep.
//
// # WHY THE SAVE IS HELD OUT HERE AND NOT BY THE FORM
//
// Everything under the drawer is drawn afresh each time it opens. A busy kept
// by the form or by the test button was forgotten when the drawer closed, so
// the same integration opened again with a request still out offered the same
// button, and a second press sent it a second time: two connections, two
// keys of which neither was shown, two real test messages. The drawer itself
// outlives its contents, so it holds the save (lib/save.ts), keyed by the
// integration, and each thing that can be sent is an act of it:
//
//   test     Test connection
//   message  Send a test message
//   save     Save, or Connect
//   remove   Remove the connection
//   key      Issue a key
//
// A key is shown once, so it is kept here too: issued while the drawer was
// shut and opened again on the same integration, it is still shown; issued
// with the drawer on another integration or closed for good, a toast says it
// is lost.

type Issued = { on: string; apiKey: string; header: string }

export function IntegrationDrawer({
  card,
  open,
  onClose,
  mayWrite,
  why,
  onChanged,
}: {
  card: CatalogueCard | null
  open: boolean
  onClose: () => void
  mayWrite: boolean
  // Why settings cannot be changed here, when they are inherited.
  why?: string
  onChanged: () => void
}) {
  const on = open && card ? card.id : null
  const save = useSave(on, card?.name ?? 'the integration')
  const [issued, setIssued] = useState<Issued | null>(null)
  // Shown once: closing the drawer puts the key away.
  useEffect(() => {
    if (on === null) setIssued(null)
  }, [on])

  return (
    <Dialog
      shape="drawer"
      kind="record"
      open={open}
      onClose={onClose}
      title={
        card ? (
          <>
            <span className="int-mark" style={{ background: card.markColor }} aria-hidden="true">
              {card.mark}
            </span>
            <span className="int-drawer-name">{card.name}</span>
            <Status tone={card.tone}>{card.statusWord}</Status>
          </>
        ) : (
          ''
        )
      }
    >
      {/* Keyed on the card, so a test result or an issued key from one
          integration is never shown in the drawer of the next. */}
      {card && (
        <Body
          key={card.id}
          card={card}
          mayWrite={mayWrite}
          why={why}
          onChanged={onChanged}
          save={save}
          issued={issued?.on === card.id ? issued : null}
          onKey={(k) => setIssued({ on: card.id, ...k })}
        />
      )}
    </Dialog>
  )
}

type Held = {
  save: DialogSave
  issued: Issued | null
  onKey: (k: { apiKey: string; header: string }) => void
}

export function Body({
  card,
  mayWrite,
  why,
  onChanged,
  save,
  issued,
  onKey,
}: {
  card: CatalogueCard
  mayWrite: boolean
  why?: string
  onChanged: () => void
} & Held) {
  const steps = STEPS[card.key] ?? card.plan?.steps
  const mapping = MAPPINGS[card.key]
  const setup = SETUP[card.key]
  const proof = PROOF_NOTE[card.key]

  return (
    <>
      <section className="int-section" aria-labelledby={card.id + '-what'}>
        <h3 id={card.id + '-what'}>What it does</h3>
        {steps ? <Steps steps={steps} /> : <p className="int-prose">{card.template?.what}</p>}
      </section>

      {mapping && (
        <section className="int-section" aria-labelledby={card.id + '-map'}>
          <h3 id={card.id + '-map'}>Field mapping</h3>
          <MappingList rows={mapping} />
        </section>
      )}

      {setup && !card.instance && (
        <section className="int-section" aria-labelledby={card.id + '-setup'}>
          <h3 id={card.id + '-setup'}>Setup</h3>
          <Steps steps={setup} />
        </section>
      )}

      {proof && <p className="int-helper">{proof}</p>}

      {card.kind === 'plan' && card.plan && <p className="int-plan-note">{card.plan.note}</p>}

      {card.kind === 'template' && (
        <Connection card={card} mayWrite={mayWrite} why={why} onChanged={onChanged} save={save} issued={issued} onKey={onKey} />
      )}

      {(card.kind === 'register' || card.kind === 'mail' || card.kind === 'entra_proof') && (
        <ManagedElsewhere card={card} />
      )}
    </>
  )
}

// The steps of what an integration does. Numbered, and with no status beside
// each: they describe the integration, not a journey somebody is part way
// along, so a word like "Not started" under each would say something false.
function Steps({ steps }: { steps: Step[] }) {
  return (
    <ol className="int-steps">
      {steps.map((s, i) => (
        <li key={s.title}>
          <span className="int-step-n" aria-hidden="true">
            {i + 1}
          </span>
          <span className="int-step-text">
            <strong>{s.title}</strong>
            <span>{s.line}</span>
          </span>
        </li>
      ))}
    </ol>
  )
}

function MappingList({ rows }: { rows: Mapping[] }) {
  return (
    <ul className="int-map">
      {rows.map((r) => (
        <li key={r.from + r.to}>
          <span className={r.fromCode ? 'kit-mono' : undefined}>{r.from}</span>
          <span className="int-map-to">to</span>
          <span className={r.toCode ? 'kit-mono' : undefined}>{r.to}</span>
        </li>
      ))}
    </ul>
  )
}

// A card that is a view of a record kept on another tab: what state that
// record is in, and the way to it.
function ManagedElsewhere({ card }: { card: CatalogueCard }) {
  const instance = card.instance
  return (
    <section className="int-section" aria-labelledby={card.id + '-conn'}>
      <h3 id={card.id + '-conn'}>Connection</h3>
      {instance && <Health instance={instance} register={card.kind === 'register'} />}
      {card.kind === 'mail' && card.template && <p className="int-helper">Sends through {card.template.displayName}</p>}
      {/* Read only here. The proof's switch is a field of the Entra ID sign-in
          record, and saving that record sends sign-in back to draft until it
          is tested again, so it is changed where sign-in is managed and never
          as a side effect of this catalogue. */}
      {card.kind === 'entra_proof' && (
        <>
          {instance ? (
            <dl className="int-facts">
              <div>
                <dt>Proof switched on</dt>
                <dd>{instance.config?.offboardingProof === 'yes' ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt>Reads the directory as</dt>
                <dd className="kit-mono">{String(instance.config?.graphClientId || 'Not set')}</dd>
              </div>
            </dl>
          ) : (
            <p className="int-helper">Needs Entra ID sign-in set up first</p>
          )}
          <p className="int-helper">Changed with the Entra ID sign-in record</p>
        </>
      )}
      {instance?.registers && instance.registers > 1 && <p className="int-helper">{instance.registers} registers of this kind</p>}
      {card.managedAt && <DialogFooter primary={{ label: card.managedAt.label, href: card.managedAt.href }} cancel={false} />}
    </section>
  )
}

// What the instance itself records about whether it works. Only what the API
// returns: a time it last worked, a time it last failed and why, and how many
// times in a row. Nothing is shown when nothing is recorded.
function Health({
  instance,
  sending,
  hideError,
  register,
}: {
  instance: ConnectorInstance
  sending?: ConnectorSending
  // A test just run shows its own answers, which include the same error.
  hideError?: boolean
  // A register's card counts its failed deliveries in the same field a
  // connector uses for failures in a row, so it is named for what it is.
  register?: boolean
}) {
  const rows: { key: string; label: string; value: ReactNode }[] = []
  if (instance.lastSuccessAt) rows.push({ key: 'ok', label: 'Last worked', value: when(instance.lastSuccessAt) })
  if (instance.lastFailureAt) rows.push({ key: 'no', label: 'Last failed', value: when(instance.lastFailureAt) })
  if (register && instance.consecutiveFailures > 0) {
    rows.push({ key: 'run', label: 'Failed deliveries', value: instance.consecutiveFailures })
  } else if (!register && instance.consecutiveFailures > 1) {
    rows.push({ key: 'run', label: 'Failures in a row', value: instance.consecutiveFailures })
  }
  if (sending?.lastTest) {
    const t = sending.lastTest
    rows.push({
      key: 'test',
      label: 'Last test message',
      value: (
        <>
          <Status tone={t.status === 'DELIVERED' || t.status === 'READ' ? 'ok' : t.status === 'FAILED' ? 'crit' : 'neutral'}>
            {sentenceCase(t.status)}
          </Status>{' '}
          {when(t.at)}
          {t.stale ? ', before the last change' : ''}
        </>
      ),
    })
  }
  const error = hideError ? '' : instance.lastError
  if (rows.length === 0 && !error) {
    return <p className="int-helper">Not tested yet</p>
  }
  return (
    <>
      {rows.length > 0 && (
        <dl className="int-facts">
          {rows.map((r) => (
            <div key={r.key}>
              <dt>{r.label}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {error && <p className="note warn int-error">{error}</p>}
    </>
  )
}

function when(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return shortDate(iso)
  return shortDate(iso) + ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
}

function sentenceCase(code: string): string {
  const s = code.toLowerCase().replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// The connection of a template card: its health, the test, the messaging proof
// and the form.
export function Connection({
  card,
  mayWrite,
  why,
  onChanged,
  save,
  issued: key,
  onKey,
}: {
  card: CatalogueCard
  mayWrite: boolean
  why?: string
  onChanged: () => void
} & Held) {
  const toast = useToast()
  const template = card.template!
  const instance = card.instance
  const sending = card.sending
  const test = save.of('test')
  const [probe, setProbe] = useState<ProbeResult | null>(null)
  const [problem, setProblem] = useState('')
  // A connected card keeps its form folded until somebody asks to change it;
  // an unconnected one shows the form straight away, because filling it in is
  // what the drawer was opened for.
  const [editing, setEditing] = useState(!instance)
  const comingNext = card.status === 'coming_next'
  const mayConnect = mayWrite && (Boolean(instance) || !comingNext)

  return (
    <section className="int-section" aria-labelledby={card.id + '-conn'}>
      <h3 id={card.id + '-conn'}>Connection</h3>

      {!mayWrite && <p className="int-helper">{why ? why : 'Only someone who may change settings can connect this'}</p>}
      {comingNext && !instance && <p className="int-helper">Delivery is not built yet, so it cannot be connected</p>}

      {instance && <Health instance={instance} sending={sending} hideError={probe != null} />}

      {instance && mayWrite && (
        <div className="int-actions">
          <button
            type="button"
            className="button"
            data-action="tprm.settings-integrations.a01"
            disabled={test.busy}
            onClick={() => {
              setProblem('')
              test.send(api.testConnector(instance.id), {
                shown: setProbe,
                done: () => onChanged(),
                failed: (err) => setProblem(err instanceof ApiError ? err.message : String(err)),
                lost: 'The test finished after the drawer was closed. Open it to see how the connection stands.',
              })
            }}
          >
            {test.busy ? 'Testing' : 'Test connection'}
          </button>
          {!editing && (
            <button type="button" className="button ghost" onClick={() => setEditing(true)} aria-expanded={editing}>
              Change settings
            </button>
          )}
        </div>
      )}

      {problem && <p className="note warn">{problem}</p>}
      {probe && <ProbeList probe={probe} />}

      {sending && (instance || sending.probeOnly) && (
        <SendingProof sending={sending} instance={instance} mayWrite={mayWrite} onChanged={onChanged} save={save} />
      )}

      {key && (
        <div className="note" role="status">
          <strong>Give the far side this key. It cannot be read again.</strong>
          <p className="mono int-wrap">
            {key.header}: {key.apiKey}
          </p>
          {/* S/4HANA sends to us through Event Mesh, which needs the address as
              well as the key, and nothing else shows it. */}
          {template.key === 'sap_s4hana' && instance && (
            <>
              <p className="int-wrap">
                In SAP Event Mesh, send Business Partner Created and Changed events to{' '}
                <span className="mono">{window.location.origin + '/v1/procurement/sap-events/' + instance.id}</span>
              </p>
              <p className="int-wrap">Use this key as the X-Api-Key header or the basic authentication password</p>
            </>
          )}
        </div>
      )}

      {mayConnect && editing && (
        <Form
          template={template}
          instance={instance}
          save={save}
          onCancel={instance ? () => setEditing(false) : undefined}
          onDone={(note) => {
            setEditing(false)
            toast(note || (instance ? 'Saved. Test it again.' : 'Connected. Test it next.'), 'ok')
            onChanged()
          }}
          onKey={onKey}
          onRemoved={() => {
            toast('Removed. Nothing goes there any more.', 'ok')
            onChanged()
          }}
        />
      )}
    </section>
  )
}

// A test's answers, each one of three: passed, refused, or unproven. Unproven
// is its own answer because folding it into either of the others is how a
// screen ends up claiming something works when nobody established it.
function ProbeList({ probe }: { probe: ProbeResult }) {
  return (
    <div className="int-probe" role="status">
      <p className="int-helper">{probe.note}</p>
      <ul>
        {probe.checks.map((c) => (
          <li key={c.check}>
            <Status tone={c.unproven ? 'warn' : c.ok ? 'ok' : 'crit'}>{c.unproven ? 'Unproven' : c.ok ? 'Passed' : 'Refused'}</Status>
            <span>
              <strong>{c.check}</strong> {c.detail}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// What a messaging card can do, and the one way to prove it: a real message
// to the operator's own phone. The card turns Connected only when the carrier
// confirms that message was delivered.
export function SendingProof({
  sending,
  instance,
  mayWrite,
  onChanged,
  save,
}: {
  sending: ConnectorSending
  instance?: ConnectorInstance
  mayWrite: boolean
  onChanged: () => void
  save: DialogSave
}) {
  const [to, setTo] = useState('')
  const [kind, setKind] = useState('vendor.reminder')
  const message = save.of('message')
  const busy = message.busy
  const [result, setResult] = useState<{ sent: boolean; note: string; detail?: string } | null>(null)

  return (
    <div className="int-sending">
      <p className="int-prose">{sending.why}</p>
      {sending.webhookUrl && (
        <p className="int-helper int-wrap">
          Receipt address for the carrier: <span className="mono">{sending.webhookUrl}</span>
        </p>
      )}
      {instance && !sending.probeOnly && mayWrite && (
        <form
          className="wizard-form"
          onSubmit={(e) => {
            e.preventDefault()
            // A second Enter while the first message is out sends no second.
            if (busy) return
            setResult(null)
            message.send(api.sendTestMessage(instance.id, to.trim(), kind), {
              shown: setResult,
              done: () => onChanged(),
              failed: (err) => setResult({ sent: false, note: err instanceof ApiError ? err.message : String(err) }),
              lost: 'The test message was answered after the drawer was closed. Open it to see whether it was sent.',
            })
          }}
        >
          <label>
            Your mobile number
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+919876543210" autoComplete="off" />
          </label>
          <label>
            Message to send
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="vendor.reminder">Questionnaire reminder</option>
              <option value="vendor.invite">Questionnaire invitation</option>
              <option value="vendor.clarification">Question about an answer</option>
              <option value="vendor.otp">One time code</option>
            </select>
          </label>
          <div className="int-actions">
            <button type="submit" className="button" data-action="tprm.settings-integrations.a02" disabled={busy || !to.trim()}>
              {busy ? 'Sending' : 'Send a test message'}
            </button>
          </div>
        </form>
      )}
      {/* Sent is not delivered: the card turns Connected only on the
          carrier's receipt, so a message that left is labelled as sent. */}
      {result && (
        <p className={'note' + (result.sent ? '' : ' warn')} role="status">
          <Status tone={result.sent ? 'info' : 'crit'}>{result.sent ? 'Sent' : 'Not sent'}</Status> {result.note}
          {result.detail ? ' ' + result.detail : ''}
        </p>
      )}
    </div>
  )
}

export function Form({
  template,
  instance,
  save,
  onCancel,
  onDone,
  onKey,
  onRemoved,
}: {
  template: ConnectorTemplate
  instance?: ConnectorInstance
  save: DialogSave
  onCancel?: () => void
  onDone: (note: string) => void
  onKey: (k: { apiKey: string; header: string }) => void
  onRemoved: () => void
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const start: Record<string, string> = {}
    for (const f of template.fields) {
      const held = instance?.config?.[f.key]
      start[f.key] = held !== undefined && held !== null ? String(held) : String(f.default ?? '')
    }
    return start
  })
  const saving = save.of('save')
  const removal = save.of('remove')
  const keying = save.of('key')
  // The three share the footer, so any one of them out locks all of it.
  const busy = saving.busy || removal.busy || keying.busy
  const [issues, setIssues] = useState<string[]>([])

  const byKey = new Map(template.fields.map((f) => [f.key, f]))
  const grouped = FIELD_GROUPS[template.key]
  const placed = new Set(grouped?.flatMap((g) => g.keys) ?? [])
  const groups: { label?: string; fields: ConnectorField[] }[] = grouped
    ? grouped.map((g) => ({ label: g.label, fields: g.keys.map((k) => byKey.get(k)).filter((f): f is ConnectorField => Boolean(f)) }))
    : []
  // Fields a group does not name still belong on the form, after the groups,
  // so a field added to the template later is never silently left off.
  const rest = template.fields.filter((f) => !placed.has(f.key))
  if (rest.length > 0) groups.push({ label: grouped ? 'Other settings' : undefined, fields: rest })

  const field = (f: ConnectorField) =>
    fieldApplies(template.key, f.key, values) ? (
      <Field key={f.key} field={f} value={values[f.key] ?? ''} onChange={(v) => setValues((old) => ({ ...old, [f.key]: v }))} />
    ) : null

  const formId = 'int-form-' + template.key
  const [asked, setAsked] = useState(false)
  // Asked about, or already being removed: a removal still out when the
  // drawer is opened again is shown as one, not as a form that can be saved.
  const removing = asked || removal.busy

  function remove() {
    if (!instance) return
    removal.send(api.removeConnector(instance.id), {
      done: () => onRemoved(),
      failed: (err) => {
        setAsked(false)
        setIssues([err instanceof ApiError ? err.message : String(err)])
      },
    })
  }

  function issue() {
    if (!instance) return
    keying.send(api.issueInboundKey(instance.id), {
      shown: (k) => onKey({ apiKey: k.apiKey, header: k.header }),
      failed: (err) => setIssues([err instanceof ApiError ? err.message : String(err)]),
      lost: 'The key was issued after the drawer was closed and cannot be shown again. Issue a new one.',
    })
  }

  return (
    <form
      id={formId}
      className="wizard-form int-form"
      onSubmit={(e) => {
        e.preventDefault()
        // Enter in a field submits the form whatever the footer's button is.
        if (busy) return
        setIssues([])
        const body: Record<string, unknown> = {}
        for (const f of template.fields) {
          const raw = (values[f.key] ?? '').trim()
          if (raw === '') continue
          body[f.key] = f.type === 'number' ? Number(raw) : raw
        }
        saving.send(instance ? api.saveConnector(instance.id, body) : api.addConnector(template.key, body), {
          done: (r) => onDone(r.note),
          failed: (err) => {
            const any = err as { issues?: { detail: string }[]; message?: string }
            setIssues(any.issues?.map((i) => i.detail) ?? [any.message ?? 'That was not saved.'])
          },
        })
      }}
    >
      {groups.map((g, i) =>
        g.label ? (
          <fieldset key={g.label} className="fieldset int-fieldset">
            <legend>{g.label}</legend>
            {g.fields.map(field)}
          </fieldset>
        ) : (
          <div key={'g' + i} className="int-fields">
            {g.fields.map(field)}
          </div>
        ),
      )}

      {issues.map((i) => (
        <p key={i} className="note warn">
          {i}
        </p>
      ))}

      {/* The form is the end of a long panel, so its buttons are the
          dialog's: Save or Connect is in view from the first field. Remove
          keeps to the far side from it and asks before it acts: pressed, the
          footer becomes the question and its two answers. */}
      <DialogFooter
        busy={busy}
        problems={issues.length}
        note={removing ? 'Removing it stops everything that goes there or comes from there.' : undefined}
        primary={
          removing
            ? { label: removal.busy ? 'Removing' : 'Remove the connection', tone: 'destructive', onClick: remove, actionId: 'tprm.settings-integrations.a06' }
            : {
                label: saving.busy ? 'Saving' : instance ? 'Save' : 'Connect',
                form: formId,
                actionId: 'tprm.settings-integrations.a03',
                actionAlso: 'tprm.settings-integrations.a04',
              }
        }
        cancel={removing ? { onClick: () => setAsked(false) } : onCancel ? { onClick: onCancel } : undefined}
        more={
          instance && !removing ? (
            <>
              <button type="button" className="button ghost int-remove" disabled={busy} onClick={() => setAsked(true)}>
                Remove
              </button>
              {template.direction !== 'OUTBOUND' && (
                <button
                  type="button"
                  className="button"
                  data-action="tprm.settings-integrations.a05"
                  disabled={busy}
                  onClick={issue}
                >
                  {keying.busy ? 'Issuing' : instance.hasInboundKey ? 'Issue a new key' : 'Issue a key'}
                </button>
              )}
            </>
          ) : undefined
        }
      />
    </form>
  )
}

function Field({ field, value, onChange }: { field: ConnectorField; value: string; onChange: (v: string) => void }) {
  return (
    <label>
      {field.label}
      {field.enum && field.enum.length > 0 ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} required={field.required}>
          {field.enum.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          autoComplete="off"
          placeholder={field.secret ? 'Paste it here' : undefined}
        />
      )}
      {field.secret && (
        <span className="field-note">
          Stored encrypted and never shown again{' '}
          <Toggletip label="How a secret is stored">
            Stored encrypted with this deployment&rsquo;s key and never shown again. An operator may give env:NAME or
            secret:NAME instead.
          </Toggletip>
        </span>
      )}
      {field.help && !field.secret && <span className="field-note">{field.help}</span>}
    </label>
  )
}
